"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsGetProductDetails = exports.googleAdsGetProductDetailsResultSchema = exports.googleAdsGetProductDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var googleAdsGetProductDetailsInputSchema = zod_1.z
    .object({
    customerId: zod_1.z
        .string()
        .nullish()
        .describe('ONLY provide if user explicitly specifies a Google Ads customer ID. DO NOT generate or guess customer IDs. Leave empty to automatically use the selected account. Must be provided by user - never invent random numbers.'),
    campaignId: zod_1.z
        .string()
        .nullish()
        .describe('Specific Shopping campaign ID to analyze. If not provided, returns data for all Shopping campaigns in the account.'),
    adGroupId: zod_1.z
        .string()
        .nullish()
        .describe('Specific ad group ID to analyze. If not provided, returns data for all ad groups.'),
    productId: zod_1.z
        .string()
        .nullish()
        .describe('Specific product ID (offer ID) to analyze. If not provided, returns data for all products.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-08-01. Optional.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-08-31. Optional.'),
    metrics: zod_1.z
        .array(zod_1.z.enum(['impressions', 'clicks', 'cost', 'conversions', 'ctr', 'cpc', 'cpm']))
        .nullish()
        .default(['impressions', 'clicks', 'cost', 'ctr'])
        .describe('Metrics to include in the response. Defaults to basic metrics.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of products to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    includeDisabled: zod_1.z
        .boolean()
        .default(false)
        .describe('Include paused, removed, or disabled products in results. Set to true to show all products regardless of status. Defaults to false.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if no product data is found. Unless specified, defaults to true.'),
        groupByDay: zod_1.z
            .boolean()
            .default(false)
            .describe('Group performance data by day. Unless specified, defaults to false.'),
    })
        .describe('Performance query options'),
})
    .describe('Get performance metrics for individual products (SKUs) in Shopping campaigns. Shows which specific products drive sales and conversions.');
exports.googleAdsGetProductDetailsArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsGetProductDetailsInputSchema);
var googleAdsGetProductDetailsResponseSchema = zod_1.z.array(zod_1.z.object({
    productId: zod_1.z.string().describe('The product ID (offer ID)'),
    productTitle: zod_1.z.string().nullish().describe('The product title'),
    productGroupId: zod_1.z.string().nullish().describe('The product group ID this product belongs to'),
    productGroupName: zod_1.z.string().nullish().describe('The product group name'),
    adGroupId: zod_1.z.string().nullish().describe('The ad group ID'),
    adGroupName: zod_1.z.string().nullish().describe('The ad group name'),
    campaignId: zod_1.z.string().nullish().describe('The campaign ID'),
    campaignName: zod_1.z.string().nullish().describe('The campaign name'),
    status: zod_1.z.string().nullish().describe('Product status'),
    performance: zod_1.z
        .object({
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
        conversions: zod_1.z.number().nullish(),
        ctr: zod_1.z.number().nullish(),
        cpc: zod_1.z.number().nullish(),
        cpm: zod_1.z.number().nullish(),
        conversionRate: zod_1.z.number().nullish(),
        conversionValue: zod_1.z.number().nullish(),
        roas: zod_1.z.number().nullish(),
    })
        .describe('Performance metrics for the product'),
    dailyBreakdown: zod_1.z
        .array(zod_1.z.object({
        date: zod_1.z.string(),
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
    }))
        .nullish()
        .describe('Daily performance breakdown if groupByDay is enabled'),
}));
exports.googleAdsGetProductDetailsResultSchema = googleAdsGetProductDetailsResponseSchema;
exports.googleAdsGetProductDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'googleAdsGetProductDetails',
    displayName: 'Google Ads Product Performance',
    displayDescription: 'Get performance metrics for individual products (SKUs) in Shopping campaigns. Shows which specific products drive sales and conversions. Automatically uses the selected account unless user provides specific customer ID.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsGetProductDetailsArgsSchema,
}, googleAdsGetProductDetailsResponseSchema);
